import {FormArray, FormControl, Validators} from '@angular/forms';
import {
  ParcelAppEntityParcelWrite,
  RoadOccupationPurposeFeeAppEntityDictionaryRoadOccupationPurposeFee,
  RoadOccupationStageAppEntityApplicationRead
} from '../../../../../../openapi';
import {CustomValidator, fromToDate} from '../../../../../../common/forms/custom.validators';
import {calcDaysBetweenDates} from '../../../../../../services/helpers';
import {RoadControlsForm, RoadControlsFormValue} from '../../components/road-controls-set/road-controls.form';
import {ExposedMapDictionary, getRatesAreaByCodes} from '../../../../../../common/exposed-map/exposed-map.component';
import {environment} from '../../../../../../../environments/environment';
import {
  RoadOccupationStagePurposeFeeItemForm,
  RoadOccupationStagePurposeFeeItemFormValue
} from "./road-occupation-stage-purpose-fee-item.form";

export function toStageFormValue(v: RoadOccupationStageAppEntityApplicationRead): RoadOccupationStageFormValue {
  return {
    id: '',
    startDate: v?.startDate || null,
    endDate: v?.endDate || null,
    dayQuantity: v?.dayQuantity || 0,

    geometry: v?.geometry || '',

    roadNumber: v?.roadNumber || '',
    roadName: v?.roadName || '',
    street: v?.street || '',

    parcels: v?.parcels || [],

    purposeFees: (v?.purposeFees || []).map((item) => ({
      width: item?.width || '',
      length: item?.length || '',
      area: item?.area || '',
      name: item?.purposeFee?.shortName || item?.purposeFee?.rateName || '',
      feeId: item?.purposeFee?.id + '' || '',
      rateId: item?.purposeFee?.rateId + '' || ''
    })),
  };
}

export interface RoadOccupationStageFormValue extends RoadControlsFormValue {
  id: string;
  startDate: Date | null;
  endDate: Date | null;
  dayQuantity: number;

  geometry: string;

  parcels: ParcelAppEntityParcelWrite[];
  purposeFees: RoadOccupationStagePurposeFeeItemFormValue[];
}

export class RoadOccupationStageForm extends RoadControlsForm {

  constructor() {
    super({
      id: new FormControl('', {nonNullable: true}),
      startDate: new FormControl(null, {validators: [Validators.required, CustomValidator.isValidDateFormat(), CustomValidator.minDate(new Date())]}),
      endDate: new FormControl(null, {validators: [Validators.required, CustomValidator.isValidDateFormat()]}),
      dayQuantity: new FormControl(null, [Validators.required, Validators.maxLength(255)]),

      geometry: new FormControl(null, environment.production ? Validators.required : []),

      parcels: new FormControl([], {
        nonNullable: true,
        validators: [
          Validators.minLength(1),
          Validators.required
        ]
      }),

      purposeFees: new FormArray([])
    }, {
      validators: [
        fromToDate('startDate', 'endDate')
      ]
    });
  }

  public get purposeFees(): FormArray {
    return this.get('purposeFees') as FormArray;
  }

  public get purposeFeesForms(): RoadOccupationStagePurposeFeeItemForm[] {
    return this.purposeFees.controls as RoadOccupationStagePurposeFeeItemForm[];
  }

  public createPurposeFeesForms(
    fees: RoadOccupationPurposeFeeAppEntityDictionaryRoadOccupationPurposeFee[],
    formValue: RoadOccupationStagePurposeFeeItemFormValue[]
  ): void {
    this.purposeFees.clear();
    fees.forEach(fee => {
      const f = new RoadOccupationStagePurposeFeeItemForm();

      const value: RoadOccupationStagePurposeFeeItemFormValue | null = formValue
        .filter(v => v.feeId === (fee.id || '')).at(0) || null;

      f.name.setValue(fee?.shortName || fee?.rateName);
      f.feeId.setValue(fee.id + '');
      f.rateId.setValue(fee?.rateId + '');

      if (value) {
        f.width.setValue(Number(value.width).toFixed(2).replaceAll('.', ','));
        f.length.setValue(Number(value.length).toFixed(2).replaceAll('.', ','));
        f.area.setValue(Number(value.area).toFixed(2).replaceAll('.', ','));

        f.calcArea();
      }
      this.purposeFees.push(f);
    })
  }

  public onLoadExposedMapDict(v: ExposedMapDictionary): void {
    this.purposeFeesForms.forEach(f => {
      const areaValue = getRatesAreaByCodes(v.rates, [+f.rateId.value]);
      if (areaValue) {
        f.length.setValue('');
        f.width.setValue('');
        f.area.setValue(areaValue);
      }
    });
  }

  public get startDate(): FormControl {
    return this.get('startDate') as FormControl;
  }

  public get endDate(): FormControl {
    return this.get('endDate') as FormControl;
  }

  public get dayQuantity(): FormControl {
    return this.get('dayQuantity') as FormControl;
  }

  public startDateChange(v: Date): void {
    if (v && this.endDate.value) {
      this.dayQuantity.setValue(
        calcDaysBetweenDates(new Date(v), new Date(this.endDate.value))
      );
    } else {
      this.dayQuantity.setValue('');
    }
  }

  public endDateChange(v: Date): void {
    if (v && this.startDate.value) {
      this.dayQuantity.setValue(
        calcDaysBetweenDates(new Date(this.startDate.value), new Date(v))
      );
    } else {
      this.dayQuantity.setValue('');
    }
  }

  public get parcels(): FormControl {
    return this.get('parcels') as FormControl;
  }

  public get geometry(): FormControl {
    return this.get('geometry') as FormControl;
  }


  public override get valid(): boolean {
    return super.valid && !!this.purposeFeesForms.filter(f => !!f.values.area).length;
  }

  public override patchFormValues(value: RoadOccupationStageFormValue): void {
    super.patchFormValues(value);
    this.purposeFeesForms.forEach(f => {
      const v = value.purposeFees.filter(v => v.feeId === f.feeId.value);
      f.patchFormValues(v.at(0) || {});
    });
  }

  public get values(): RoadOccupationStageFormValue {
    return {
      id: this.value.id,
      startDate: this.value.startDate,
      endDate: this.value.endDate,
      dayQuantity: this.dayQuantity.value,

      geometry: this.geometry.value,

      roadName: this.roadName.value,
      roadNumber: this.roadNumber.value,
      street: this.value.street,

      parcels: this.parcels.value,

      purposeFees: this.purposeFeesForms.map(v => v.values)
    };
  }
}
